import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import DeselectIcon from '@mui/icons-material/Deselect';
import { useTranslation } from 'react-i18next';
import { TableHeaderTooltip } from '@applications-terrains/birdz-react-library';

export type DndToolbarProps = {
  hasOpen: boolean;
  authorizedLength: number;
  unauthorizedLength: number;
  selectionSize: number;
  onReset: () => void;
  onCloseAll: () => void;
  onClearSelection: () => void;
  /** Titre de la section (ex: "Autorisations"). */
  title?: string;
  /** Tooltip du bouton reset (défaut: t('reset')). */
  resetTooltip?: string;
  /** Tooltip du bouton close-all (défaut: t('closeAll')). */
  closeAllTooltip?: string;
  /** Tooltip du bouton deselect (défaut: t('deselectAll')). */
  deselectTooltip?: string;
  /** Mode consultation : masque reset et deselect, conserve close-all. */
  readOnly?: boolean;
};

export const DndToolbar = ({
  hasOpen,
  authorizedLength,
  unauthorizedLength,
  selectionSize,
  onReset,
  onCloseAll,
  onClearSelection,
  title,
  resetTooltip,
  closeAllTooltip,
  deselectTooltip,
  readOnly
}: DndToolbarProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      {title && (
        <Typography variant="overline" ml={2}>
          {title}
        </Typography>
      )}
      <Box
        sx={{
          mr: hasOpen ? -1.5 : -1,
          display: 'flex',
          transition: 'margin 0.3s ease',
          ml: title ? undefined : 'auto'
        }}
      >
        {!readOnly && (
          <Box
            data-testid="reset-container"
            sx={{
              mb: '2px',
              width: authorizedLength > 0 || unauthorizedLength > 0 ? 34 : 0,
              transition: 'width 0.3s ease',
              overflow: 'hidden'
            }}
          >
            <TableHeaderTooltip
              title={
                <Box
                  data-testid="reset-tooltip"
                  sx={{ py: 1, fontSize: '0.7rem', color: '#666666' }}
                >
                  {resetTooltip ?? t('reset')}
                </Box>
              }
              element={
                <IconButton
                  data-testid="reset-btn"
                  size="small"
                  sx={{ height: 34 }}
                  onClick={onReset}
                >
                  <RestartAltIcon sx={{ mt: '-2px' }} />
                </IconButton>
              }
            />
          </Box>
        )}
        <Box
          data-testid="closeall-container"
          sx={{
            mb: '2px',
            width: hasOpen ? 34 : 0,
            transition: 'width 0.3s ease',
            overflow: 'hidden'
          }}
        >
          <TableHeaderTooltip
            title={
              <Box
                data-testid="closeall-tooltip"
                sx={{ py: 1, fontSize: '0.7rem', color: '#666666' }}
              >
                {closeAllTooltip ?? t('closeAll')}
              </Box>
            }
            element={
              <IconButton
                data-testid="closeall-btn"
                size="small"
                sx={{ height: 34 }}
                onClick={onCloseAll}
              >
                <UnfoldLessIcon />
              </IconButton>
            }
          />
        </Box>
        {!readOnly && (
          <Box
            data-testid="deselect-container"
            sx={{
              mb: '2px',
              width: selectionSize > 0 ? 34 : 0,
              transition: 'width 0.3s ease',
              overflow: 'hidden'
            }}
          >
            <TableHeaderTooltip
              title={
                <Box
                  data-testid="deselect-tooltip"
                  sx={{ py: 1, fontSize: '0.7rem', color: '#666666' }}
                >
                  {deselectTooltip ?? t('deselectAll')}
                </Box>
              }
              element={
                <IconButton
                  data-testid="deselect-btn"
                  size="small"
                  sx={{ height: 34 }}
                  onClick={onClearSelection}
                >
                  <DeselectIcon />
                </IconButton>
              }
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DndToolbar;
